import { ObservableSubscription, useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Subject, of } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';
import {
  RegisterAndSubscribeToMachineBindingDocument,
  RegisterAndSubscribeToMachineBindingSubscription,
  RegisterAndSubscribeToMachineBindingSubscriptionVariables,
  UpdateMachineBindingContextDocument,
  UpdateMachineBindingContextMutation,
  UpdateMachineBindingContextMutationVariables,
} from '../../graphql/generated';
import { subscriptionDefaultErrorHandler } from '../../subscription/apollo-subscription.manager';

interface SynchronousBindingReturnVal<BindingContext, BindingResult> {
  send: SynchronousBindingCallback<BindingContext, BindingResult | undefined>;
  close: () => void;
}

type SynchronousBindingCallback<BindingContext, BindingResult> = (
  machineId: string,
  context: BindingContext
) => Promise<BindingResult>;

export interface SynchronousBindingOptions<BindingResult> {
  namespace: string;
  type: string;
  closeImmediately?: boolean;
  onResult?: (result: BindingResult | undefined) => void;
}

/**
 * Hook designed to allow synchronous invocations of a binding. By default, binding results
 * are asynchronous as the results are sent back through the subscription on the server. In
 * certain use cases we might want to execute a binding and get an immediate result which this
 * allows for. We also might want to keep the binding long-lived and "update" the context values
 * without having to recreate the subscription and binding each time (writing to cache for example).
 * This hook provides an abstraction over bindings to support those use cases.
 *
 * @param options
 * @returns
 */
const useSynchronousBindingLazy = <BindingContext, BindingResult>(
  options: SynchronousBindingOptions<BindingResult>
): SynchronousBindingReturnVal<BindingContext, BindingResult | undefined> => {
  const client = useApolloClient();

  const subscriptionRef = useRef<ObservableSubscription | undefined>();
  const bindingIdRef = useRef<string | undefined>();

  const closeSubscription = useCallback(() => {
    if (!subscriptionRef.current?.closed) {
      subscriptionRef.current?.unsubscribe();
    }

    // reset our subscription ref since we have closed it out
    subscriptionRef.current = undefined;
    bindingIdRef.current = undefined;
  }, []);

  const send = useCallback(
    async (machineId: string, context: BindingContext) => {
      const { namespace, type, onResult } = options;

      // by default we will immediately close out the subscription after invocation vs leaving it
      // open for future requests
      const closeImmediately = options?.closeImmediately ?? true;

      const subject$ = new Subject<BindingResult | undefined>();

      // if we aren't already subscribed then the first invocation should start a subscription
      // using the desired context so that we get a new binding created. If the options were configured
      // to not close immediately (after the first result) then subsquent invocations of the callback should
      // use a mutation to send the updated binding context to the cloud connector by using the binding id
      // returned from the subscription

      if (bindingIdRef.current) {
        // manually invoke client mutation instead of using hook. when using a hook it will cause this hook to re-render
        // and can create an infinite render cycle. there is no need to await this call since we pull actually results
        // through the subscription since the binding will publish the result from the cloud connector back to the server
        client.mutate<
          UpdateMachineBindingContextMutation,
          UpdateMachineBindingContextMutationVariables
        >({
          mutation: UpdateMachineBindingContextDocument,
          variables: {
            input: {
              machineId,
              bindingId: bindingIdRef.current,
              context,
            },
          },
          fetchPolicy: 'network-only',
        });
      } else if (subscriptionRef.current === undefined) {
        const observable$ = client.subscribe<
          RegisterAndSubscribeToMachineBindingSubscription,
          RegisterAndSubscribeToMachineBindingSubscriptionVariables
        >({
          query: RegisterAndSubscribeToMachineBindingDocument,
          variables: {
            input: {
              machineId,
              binding: {
                namespace,
                type,
                context,
              },
            },
          },
        });

        subscriptionRef.current = observable$.subscribe({
          next: (result) => {
            const bindingResult =
              result?.data?.registerAndSubscribeToMachineBindingResult;

            // store binding id so subsequent updates can use a mutation instead of
            // opening a new subscription and binding
            bindingIdRef.current = bindingResult?.bindingId;

            const data = bindingResult?.data ?? undefined;
            subject$.next(data);

            // If we have onResult and subscription stays open, forward all results
            if (onResult) {
              onResult(data);
            }
          },
          error: (error) => {
            subscriptionDefaultErrorHandler(error);
            subject$.next(undefined);
          },
        });
      }

      return new Promise<BindingResult | undefined>((resolve) => {
        subject$
          .pipe(
            timeout(2000),
            take(1),
            catchError(() => {
              return of(undefined);
            })
          )
          .subscribe((r) => {
            if (closeImmediately) {
              closeSubscription();
            }

            resolve(r);
          });
      });
    },

    // stringify the options so we can have our hook dependency on a primitive which can prevent
    // unwanted renders from happening since the object reference could change even though the
    // values are the same

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, closeSubscription, JSON.stringify(options)]
  );

  /**
   * If this hook unmounts be sure to close out subscription if not already closed
   */
  useEffect(() => {
    return () => {
      closeSubscription();
    };
  }, [closeSubscription]);

  const returnVal = useMemo<
    SynchronousBindingReturnVal<BindingContext, BindingResult | undefined>
  >(() => {
    return {
      send,
      close: closeSubscription,
    };
  }, [send, closeSubscription]);

  return returnVal;
};

export { useSynchronousBindingLazy };
